import React, { useState } from "react";
import { Camera, ChevronDown, ChevronUp, Clock, Sliders, Users, ExternalLink, MapPin } from "lucide-react";

const LORA = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

function Row({ icon: Icon, label, children, testId }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-2">
      <Icon size={13} color="#C17F59" strokeWidth={1.5} className="flex-shrink-0 mt-0.5" />
      <p className="text-xs text-[#5C554E] leading-relaxed" style={LORA} data-testid={testId}>
        <strong className="text-[#2B2623]">{label} :</strong> {children}
      </p>
    </div>
  );
}

const mapsUrl = (spot, destination) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([spot.nom, destination].filter(Boolean).join(", "))}`;

function SpotEntry({ spot, index, destination }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border border-[#DCD4C6] bg-[#FDFAF7] overflow-hidden" style={{ borderRadius: "3px" }} data-testid={`photo-spot-${index}`}>
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F9F6F0] transition-colors"
        onClick={() => setOpen((o) => !o)}
        data-testid={`photo-spot-toggle-${index}`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Camera size={13} color="#C17F59" strokeWidth={1.5} className="flex-shrink-0" />
          <span className="text-sm font-semibold text-[#2B2623] truncate" style={SERIF}>{spot.nom}</span>
        </div>
        {open ? <ChevronUp size={13} color="#9A8F86" strokeWidth={1.5} /> : <ChevronDown size={13} color="#9A8F86" strokeWidth={1.5} />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 space-y-2.5 border-t border-[#F1EBE1]">
          <Row icon={Clock} label="Meilleur horaire" testId={`photo-time-${index}`}>{spot.meilleur_horaire}</Row>
          <Row icon={Users} label="Affluence" testId={`photo-crowd-${index}`}>{spot.affluence}</Row>
          <Row icon={Sliders} label="Réglages" testId={`photo-settings-${index}`}>{spot.reglages}</Row>
          {spot.conseil && (
            <p className="text-xs text-[#5C554E] italic leading-relaxed border-l-2 border-[#C17F59] pl-3" style={LORA} data-testid={`photo-tip-${index}`}>
              {spot.conseil}
            </p>
          )}
          <a
            href={mapsUrl(spot, destination)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-[#F9F6F0] bg-[#C17F59] hover:bg-[#A86A46] px-3 py-1.5 mr-3 transition-colors"
            style={{ ...LORA, borderRadius: "2px" }}
            data-testid={`photo-maps-${index}`}
          >
            <MapPin size={11} strokeWidth={1.5} /> Ouvrir dans Google Maps
          </a>
          {/^https?:\/\//.test(spot.source || "") && (
            <a href={spot.source} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#8B5E34] hover:underline" style={LORA} data-testid={`photo-source-${index}`}>
              <ExternalLink size={11} strokeWidth={1.5} /> Source
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function PhotoSpotCard({ spots, destination }) {
  const valid = (spots || []).filter((s) => s && s.nom);
  if (valid.length === 0) return null;
  return (
    <div className="mt-4 pt-4 border-t border-[#F1EBE1]" data-testid="photo-spot-section">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 bg-[#F1EBE1] border border-[#DCD4C6] flex items-center justify-center flex-shrink-0" style={{ borderRadius: "2px" }}>
          <Camera size={12} color="#C17F59" strokeWidth={1.5} />
        </div>
        <p className="text-xs uppercase tracking-widest text-[#8B5E34]" style={LORA}>Spots photo du jour</p>
      </div>
      <div className="space-y-2">
        {valid.map((spot, i) => <SpotEntry key={i} spot={spot} index={i} destination={destination} />)}
      </div>
    </div>
  );
}
